import type { HouseListItem } from '../../../api/station/stationApiTypes';
import { ROOM_PRODUCT_USE_CASES } from '../../services/serviceConfigurationRules';

/**
 * Houses in the objects overview (plan room-houses-and-combining-rooms §0,
 * PO 2026-10-08).
 *
 * The overview groups objects by station (`groupByStation`), so a house with
 * two or more rooms is already a row. Two cases were invisible: a house with
 * one room collapses into its room's row like any one-object station, and a
 * house without rooms has no object to group, so no row at all — a record
 * nobody knows exists. The house list says which stations are houses.
 *
 * Only real houses (`container_type = 'house'`) are listed by the server; the
 * automatic one-room stations a room stands on until it joins a house are
 * not, so a tenant without houses sees the overview exactly as before.
 */

/** Station id → house name, for the one-room marker. */
export const houseNamesById = (houses: HouseListItem[]): Map<string, string> =>
    new Map(houses.map((house) => [house.id, house.name]));

/**
 * Houses without rooms, as the filters of the overview leave them. A house
 * has no use case of its own: it shows while the filters admit rooms at all,
 * and a search matches its name or address.
 */
export const emptyHousesToShow = (
    houses: HouseListItem[],
    filters: { searchQuery: string, selectedUseCase: string, allowedUseCases?: string[] },
): HouseListItem[] => {
    const admitsRooms = (filters.selectedUseCase === 'all'
        || ROOM_PRODUCT_USE_CASES.has(filters.selectedUseCase))
        && (!filters.allowedUseCases?.length
            || filters.allowedUseCases.some((useCase) => ROOM_PRODUCT_USE_CASES.has(useCase)));
    if (!admitsRooms) return [];

    const query = filters.searchQuery.trim().toLowerCase();
    return houses
        .filter((house) => house.roomCount === 0)
        .filter((house) => !query || [
            house.name, house.address?.street, house.address?.city, house.address?.postalCode,
        ].some((value) => value?.toLowerCase().includes(query)))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
};
